import { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import { cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
} from '../utils/tuition-assistance/fields';
import CommentableModel from './commentable.ts';
import type UserModel from './user.ts';
import type Intl from 'ember-intl/services/intl';
import type { Store } from '@warp-drive/core';
import type TASProgramInstanceModel from './tas-program-instance.ts';
import type TasProgramLimitModel from './tas-program-limit.ts';
import type TASCourseModel from './tas-course.ts';
import type TASAssetModel from './tas-asset.ts';
import type TagModel from './tag.ts';
import type AttachmentModel from './attachment.ts';
import type TasAssistanceModel from './tas-assistance.ts';
import type AiRecommendationModel from './ai-recommendation.ts';
import calculatePrePercentageValue from '../utils/tuition-assistance/calculate-pre-percentage-value';
import type {
  CustomFieldSignature,
  TransitionLogSignature,
  ScholarshipsReceivedField,
  StringSingleValueField,
  NumberSingleValueField,
  ApplicationApprovalSummary,
  ApplicationApprovalSummaryApprover,
  TasApplicationState,
  TasApplicationPaymentState,
  // TODO: We should be looking at assistance always. This is a bug.
  PaymentStateLogSignature,
  BooleanSingleValueField,
  ClaimsFinancialsSingleValueField,
  TasClaimsFinancials,
  TasAppHistory,
} from '../types/tuition-assistance';
import { createDefaultClaimsFinancialsWithBenefitPercent } from '../utils/tuition-assistance/claims-financials-calculator';
import ClaimsFinancialsCalculator from '../utils/tuition-assistance/claims-financials-calculator';
import { Type } from '@warp-drive/core-types/symbols';
import type PcsCardModel from './pcs-card.ts';
import type { ChipSignature } from 'tio-reimagined/components/chip';

export type TASApplicationModelFieldsSignature = {
  APPLICATION_NAME: StringSingleValueField;
  CLAIMS_FINANCIALS: ClaimsFinancialsSingleValueField;
  COURSES_BEGIN_DATE: StringSingleValueField;
  COURSES_END_DATE: StringSingleValueField;
  INVOICE_DATE: StringSingleValueField;
  SCHOLARSHIPS_RECEIVED: ScholarshipsReceivedField;
  PRE_APPROVED_TOTAL: NumberSingleValueField;
  CARD_PRE_APPROVED_TOTAL: NumberSingleValueField;
  CARD_FINAL_APPROVED_TOTAL: NumberSingleValueField;
  FINAL_APPROVED_TOTAL: NumberSingleValueField;
  WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT: BooleanSingleValueField;
  WAIVE_EVIDENCE_APPROVAL_REQUIREMENT: BooleanSingleValueField;
  EVIDENCE_APPROVAL_WAIVED_BY: NumberSingleValueField; // User ID
  SEMESTER_CODE: StringSingleValueField;
  EXPENSE_TYPE: StringSingleValueField;
  GRADE_POINT_AVERAGE?: NumberSingleValueField;
  PAYMENT_TYPE_FOR_DIRECT_PAY: StringSingleValueField;
  MARKED_FOR_AUDIT?: BooleanSingleValueField;
  RECOMMENDED_ACTIONS_TAKEN?: RecommendedActionsTakenField;
  VOUCHER_AMOUNT?: NumberSingleValueField;
};

export type ApprovedTotalLogSignature = {
  field_name: 'PRE_APPROVED_TOTAL' | 'FINAL_APPROVED_TOTAL';
  from: number;
  to: number;
  reason: string;
  timestamp: string;
  by_user_id: UserModel['id'];
  via_method: string;
};

export type RecommendedActionTaken = {
  ai_recommendation_id?: string | number;
  action?: string;
  recommended_to?: string;
  action_taken?: string;
  recommendation_summary?: Record<string, unknown>;
  timestamp?: string;
};

type RecommendedActionsTakenField = {
  values: RecommendedActionTaken[];
};

export type ApplicationAmounts = {
  requested: number;
  approved_for_payment: number;
  course_cash: {
    reserved: number;
    used: number;
    refunded: number;
  };
  pending_payment: number;
  paid: number;
  refund_requested: number;
  refund_paid_back: number;
  refund_forgiven: number;
  refund_cancelled: number;
  refund_limit: number;
  flags: {
    request_payment_enabled: boolean;
    request_repayment_enabled: boolean;
  };
};
const programTypesWhereAttendIsDraft = new Set(['TAS.ProgramType.2', 'TAS.ProgramType.4.2']);

type IntentKey = string;

const formatStateKey = (
  state: TasApplicationState | TasApplicationPaymentState,
  fromState?: TasApplicationState,
  typeClassification?: string
): string => {
  if (state === 'TAS.ApplicationState.ATTEND') {
    if (typeClassification && programTypesWhereAttendIsDraft.has(typeClassification)) {
      // behave exactly like DEFINE_COURSES / "In Draft"
      return 'TAS.ApplicationState.DEFINE_COURSES';
    }

    switch (fromState) {
      case 'TAS.ApplicationState.COURSES_NOT_APPROVED':
        return 'CHANGES_REQUESTED';
      case 'TAS.ApplicationState.COURSES_APPROVED':
        return 'IN_PROGRESS';
      case 'TAS.ApplicationState.EVIDENCE_NOT_APPROVED':
        return 'EVIDENCE_NOT_APPROVED';
      default:
        return 'HIDDEN';
    }
  }

  return state;
};

const tasIntentMap: Record<IntentKey, ChipSignature['Args']['intent']> = {
  // application states
  'TAS.ApplicationState.CREATED': 'primary',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': 'primary',
  'TAS.ApplicationState.FULFILLED': 'success',
  'TAS.ApplicationState.ATTEND': 'success',
  'TAS.ApplicationState.COURSES_NOT_APPROVED': 'warning',
  CHANGES_REQUESTED: 'default',
  IN_PROGRESS: 'success',
  CREATED: 'primary',
  DRAFT: 'primary', // treat draft similarly to created
  'TAS.ApplicationState.DEFINE_COURSES': 'default',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': 'primary',
  'TAS.ApplicationState.COURSES_APPROVED': 'success',
  'TAS.ApplicationState.COURSES_REJECTED': 'danger',
  'TAS.ApplicationState.EVIDENCE_APPROVED': 'success',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': 'danger',
  'TAS.ApplicationState.EVIDENCE_REJECTED': 'danger',
  'TAS.ApplicationState.REJECTED_PER_POLICY': 'danger',
  'TAS.ApplicationState.ABANDONED': 'warning',
  HIDDEN: 'default',
  // payment states
  'TAS.ApplicationPaymentState.NONE': 'default',
  'TAS.ApplicationPaymentState.UNPAID': 'default',
  'TAS.ApplicationPaymentState.PAID_IN_PART': 'success',
  'TAS.ApplicationPaymentState.PAID_IN_FULL': 'success',
  'TAS.ApplicationPaymentState.REFUND_REQUESTED': 'danger',
};

const tasStateLabelMap: Record<IntentKey, string> = {
  // application states
  'TAS.ApplicationState.CREATED': 'Created',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': 'Pending Evidence Approval',
  'TAS.ApplicationState.FULFILLED': 'Complete',
  'TAS.ApplicationState.ATTEND': 'Attending',
  'TAS.ApplicationState.COURSES_NOT_APPROVED': 'Not Approved',
  CHANGES_REQUESTED: 'Change Requested',
  IN_PROGRESS: 'In Progress',
  CREATED: 'Created',
  DRAFT: 'Draft',
  'TAS.ApplicationState.DEFINE_COURSES': 'In Draft',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': 'Pending Pre-Approval',
  'TAS.ApplicationState.COURSES_APPROVED': 'Approved',
  'TAS.ApplicationState.COURSES_REJECTED': 'Application Rejected',
  'TAS.ApplicationState.EVIDENCE_APPROVED': 'Evidence Approved',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': 'Evidence Not Approved',
  'TAS.ApplicationState.EVIDENCE_REJECTED': 'Evidence Rejected',
  'TAS.ApplicationState.REJECTED_PER_POLICY': 'Rejected Per Policy',
  HIDDEN: 'Hidden',
  'TAS.ApplicationState.ABANDONED': 'Abandoned',
  // payment states
  'TAS.ApplicationPaymentState.NONE': 'No Payment',
  'TAS.ApplicationPaymentState.UNPAID': 'Unpaid',
  'TAS.ApplicationPaymentState.PAID_IN_PART': 'Partially Paid',
  'TAS.ApplicationPaymentState.PAID_IN_FULL': 'Paid',
  'TAS.ApplicationPaymentState.REFUND_REQUESTED': 'In Repayment',
};

const firstTimeSubmissionMap: Record<string, string> = {
  'TAS.ProgramType.2': 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
  'TAS.ProgramType.4.2': 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
  'TAS.ProgramType.3': 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ProgramType.4.3': 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
};

const firstApprovalDateMap: Record<string, string> = {
  'TAS.ProgramType.2': 'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ProgramType.4.2': 'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ProgramType.3': 'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ProgramType.4.3': 'TAS.ApplicationState.COURSES_APPROVED',
};

export default class TasApplicationModel extends CommentableModel {
  @service declare intl: Intl;
  @service declare store: Store;

  @attr declare state: TasApplicationState;
  @attr declare fields: TASApplicationModelFieldsSignature;
  @attr declare customFields: CustomFieldSignature[];
  @attr declare transitionLog: TransitionLogSignature[];
  // TODO: We should be looking at assistance always. This is a bug.
  @attr declare paymentStateLog: PaymentStateLogSignature[];
  @attr declare approvedTotalLog: ApprovedTotalLogSignature[];
  @attr declare createdAt: string;
  @attr declare updatedAt: string;
  @attr declare coursesApproval: ApplicationApprovalSummary;
  @attr declare evidenceApproval: ApplicationApprovalSummary;
  @attr declare migrationApplicationId: string;
  @attr declare isTrial: boolean;
  @attr declare paymentState: TasApplicationPaymentState;
  @attr declare history: TasAppHistory;
  @attr declare readyForPayment: boolean;
  @attr declare ignorePolicies: boolean;
  @attr declare amounts: ApplicationAmounts;
  @attr declare lpnDiscountActivated: boolean;

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('tas-program-instance', {
    async: false,
    inverse: 'tasApplications',
  })
  declare tasProgramInstance: TASProgramInstanceModel;
  @belongsTo('tas-program-limit', { async: false, inverse: 'tasApplications' })
  declare tasProgramLimit: TasProgramLimitModel;
  @hasMany('tas-assistance', { async: false, inverse: 'tasApplication' })
  declare tasAssistances: TasAssistanceModel[];
  @hasMany('tas-course', { async: false, inverse: 'tasApplication' })
  declare tasCourses: TASCourseModel[];
  @hasMany('tas-asset', { async: false, inverse: 'tasApplications' })
  declare tasAssets: TASAssetModel[];
  @hasMany('attachment', { async: false, inverse: null })
  declare attachments: AttachmentModel[];
  @hasMany('tag', {
    async: false,
    inverse: 'taggable',
    as: 'taggable',
  })
  declare tags: TagModel[];
  @hasMany('pcs-card', { async: false, inverse: 'tasApplication' })
  declare pcsCards: PcsCardModel[];
  @hasMany('ai-recommendation', { async: false, inverse: 'recommendable' })
  declare aiRecommendations: AiRecommendationModel[];

  /**************************
   **  Computed Properties **
   **************************/

  get activeCourses() {
    return this.tasCourses.filter((course) => course.isActive);
  }

  get applicant(): string {
    return getSingleValueForTasField('NAME', this.tasProgramInstance.fields);
  }

  get applicationName(): string {
    return getSingleValueForTasField('APPLICATION_NAME', this.fields);
  }

  @cached
  get displayName() {
    return (
      this.applicationName ??
      getSingleValueForTasField('PROGRAM_SPECIALIZATION', this.tasProgramInstance?.fields) ??
      getSingleValueForTasField('PROGRAM_MAJOR', this.tasProgramInstance?.fields) ??
      this.tasProgramInstance?.tasProgramTemplate?.programName ??
      this.intl.formatDate(this.createdAt, {
        month: 'long',
        year: 'numeric',
      })
    );
  }

  get coursesBeginDate(): string {
    return getSingleValueForTasField('COURSES_BEGIN_DATE', this.fields);
  }

  get expenseType(): string {
    return getSingleValueForTasField('EXPENSE_TYPE', this.fields);
  }

  get gradePointAverage(): number {
    return getSingleValueForTasField('GRADE_POINT_AVERAGE', this.fields);
  }

  get isDraft() {
    return this.state === 'TAS.ApplicationState.DEFINE_COURSES';
  }

  get isApproved() {
    return this.approvedStates.has(this.state);
  }

  get netPaidAmount(): number {
    const { paid, refund_requested, refund_forgiven, refund_paid_back } = this.amounts;
    if (refund_requested > 0) {
      return -(refund_requested - refund_forgiven - refund_paid_back);
    } else {
      return paid;
    }
  }

  get isPaid() {
    return this.tasAssistances.some(
      (assistance) => assistance.state === 'TAS.AssistanceState.SETTLED'
    );
  }

  get isRepayable() {
    return this.tasAssistances.some(
      (assistance) => assistance.state === 'TAS.AssistanceState.REPAYABLE'
    );
  }

  get canCancelApplication() {
    const allowedStates = this.canDeleteAppStates;
    if (!this.isPrepaidProgram) allowedStates.add('TAS.ApplicationState.ATTEND');

    return allowedStates.has(this.state);
  }

  get canSubmitEvidence() {
    const courseApprovalRequired =
      this.tasProgramInstance?.tasProgramTemplate?.courseApprovalRequired;

    const validStates = ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.COURSES_APPROVED'];

    if (!courseApprovalRequired) {
      validStates.push('TAS.ApplicationState.PENDING_COURSES_APPROVAL');
    }

    return validStates.includes(this.state);
  }

  get canSubmitForPayment() {
    const courseApprovalRequired =
      this.tasProgramInstance?.tasProgramTemplate?.courseApprovalRequired;

    const validStates = ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.COURSES_APPROVED'];

    if (!courseApprovalRequired) {
      validStates.push('TAS.ApplicationState.PENDING_COURSES_APPROVAL');
    }

    return validStates.includes(this.state);
  }

  get approverCanEditApplication() {
    return this.approverCanEditStates.has(this.state);
  }

  get fieldForEditableApprovedTotal() {
    if (this.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return 'PRE_APPROVED_TOTAL';
    }
    if (this.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return 'FINAL_APPROVED_TOTAL';
    }
    return 'FINAL_APPROVED_TOTAL';
  }

  get cardPreApprovedTotal() {
    return getSingleValueForTasField('CARD_PRE_APPROVED_TOTAL', this.fields) || 0;
  }

  get cardFinalApprovedTotal() {
    return getSingleValueForTasField('CARD_FINAL_APPROVED_TOTAL', this.fields);
  }

  get isCourseGradeRequired() {
    return this.tasProgramInstance?.tasProgramTemplate?.courseGradeRequired;
  }

  get isMissingCourseGrades() {
    if (this.activeCourses.length === 0) {
      return false;
    }
    return !this.activeCourses.every((course) => course.hasGrades);
  }

  get isEvidenceRequired() {
    if (getSingleValueForTasField('WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', this.fields) === true) {
      return false;
    }

    return this.tasProgramInstance?.tasProgramTemplate?.requireAttachmentsForCourseCompletion;
  }

  get isPreApprovalAttachmentsRequired() {
    return this.tasProgramInstance?.tasProgramTemplate?.requireAttachmentsForCourseApproval;
  }

  get isPrepaidProgram() {
    return this.tasProgramInstance?.tasProgramTemplate?.programPaymentType === 'ON_ATTEND';
  }

  get paymentMethod() {
    return (
      getSingleValueForTasField('PAYMENT_TYPE_FOR_DIRECT_PAY', this.fields) ||
      'TAS.AssistanceManner.REIMBURSEMENT'
    );
  }

  @cached
  get evidenceAssets() {
    // evidenceAssets for TYPE 2 programs are stored at the application level
    return Object.freeze(
      this.tasAssets.filter(
        (asset) =>
          !asset.attachmentDeletedAt &&
          (asset.classification === 'COMPLETION_DOCUMENTS' ||
            asset.classification === 'APPLICATION_LIFECYCLE_DOCUMENTS')
      )
    );
  }

  @cached
  get applicationApprovalAssets() {
    // approval Assets for TYPE 3 programs are stored at the application level
    return Object.freeze(
      this.tasAssets.filter(
        (asset) =>
          !asset.attachmentDeletedAt &&
          (asset.classification === 'COURSE_APPROVAL_DOCUMENTS' ||
            asset.classification === 'APPLICATION_LIFECYCLE_DOCUMENTS')
      )
    );
  }

  get typeClassification() {
    return this.tasProgramInstance?.tasProgramTemplate?.typeClassification || '';
  }

  get requiresGPA() {
    const allowableType =
      this.typeClassification === 'TAS.ProgramType.4.2' ||
      this.typeClassification === 'TAS.ProgramType.2';
    return allowableType && this.tasProgramInstance?.tasProgramTemplate?.calculateTotalByGPA;
  }

  get hasRequiredEvidenceAttachments() {
    if (!this.isEvidenceRequired) {
      return true;
    }

    const hasApplicationEvidence = !!this.evidenceAssets.length;

    const allCoursesHaveEvidence = this.activeCourses.every(
      (course) => !!course.evidenceAssets.length
    );

    // If type 2/3, check for evidence at the application level or for all courses
    // doing this to make it backwards compatible with legacy programs
    return allCoursesHaveEvidence || Boolean(this.typeClassification && hasApplicationEvidence);
  }

  get hasPreApprovalAttachments() {
    return !!this.applicationApprovalAssets?.length;
  }

  get isMissingRequiredEvidence() {
    return !this.hasRequiredEvidenceAttachments;
  }

  get hasRequiredPreApprovalAttachments() {
    if (!this.isPreApprovalAttachmentsRequired) {
      return true;
    }

    const hasApplicationApprovalAssets = !!this.applicationApprovalAssets.length;

    const allCoursesHaveApprovalAssets = this.activeCourses.every(
      (course) => !!course.approvalAssets.length
    );

    // If type 2/3/4, check for evidence at the application level or for all courses
    // doing this to make it backwards compatible with legacy programs
    return this.typeClassification ? hasApplicationApprovalAssets : allCoursesHaveApprovalAssets;
  }

  get displayNameOrSemesterCode() {
    const applicationName = getSingleValueForTasField('APPLICATION_NAME', this.fields) as string;

    if (this.semesterCodeIsVisible && this.semesterCode) {
      if (applicationName) {
        return `${this.semesterCode} - ${applicationName}`;
      }
      return this.semesterCode;
    }

    if (applicationName) {
      return applicationName;
    }

    return this.intl.formatDate(this.createdAt, {
      month: 'long',
      year: 'numeric',
    });
  }

  get latestStateChangeLabel() {
    const latestTransition =
      this.transitionLog?.length > 0 ? this.transitionLog[this.transitionLog.length - 1] : null;
    return latestTransition?.to || '';
  }

  get latestStateChangeDate() {
    const latestTransition =
      this.transitionLog?.length > 0 ? this.transitionLog[this.transitionLog.length - 1] : null;

    // logic to handle multiple approvers required so that the latest state change date matches the submitted for course approval date if it's still undergoing the approval process
    if (
      latestTransition?.from === 'TAS.ApplicationState.COURSES_APPROVED' &&
      latestTransition?.to === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL'
    ) {
      return this.applicationSubmittedDate;
    }

    return latestTransition?.timestamp || '';
  }

  get latestStateChangePerson() {
    const latestTransition =
      this.transitionLog?.length > 0 ? this.transitionLog[this.transitionLog.length - 1] : null;
    return latestTransition?.reason?.by?.name || '';
  }

  get applicationSubmittedDate() {
    const pendingState = firstTimeSubmissionMap[this.typeClassification];
    const transition = this.transitionLog.find((transition) => transition.to === pendingState);

    return transition?.timestamp || '';
  }

  get approvedDate() {
    const pendingState = firstApprovalDateMap[this.typeClassification];
    const transition = this.transitionLog.find((transition) => transition.to === pendingState);

    return transition?.timestamp || '';
  }

  get requestedTotalAllCourses() {
    const sum = this.activeCourses
      .map((course) => course.courseTotal)
      .reduce((a, b) => {
        return a + b;
      }, 0);
    // This is in pennies, we should never have a decimal.
    // Rounding this here in case migrated data has decimals.
    return Math.round(sum);
  }

  get requestedTotal() {
    const totalAll = this.requestedTotalAllCourses;
    if (totalAll === 0) return 0;

    const baseTotal = Math.max(0, totalAll - this.scholarshipsTotal);

    const template = this.tasProgramInstance?.tasProgramTemplate;
    if (template?.calculateTotalByPercent) {
      const percent = template.reimbursementPercentage ?? 100;
      return Math.round((baseTotal * percent) / 100);
    }

    return Math.round(baseTotal);
  }

  // check the amounts.course_cash reserved - used
  get courseCashAvailableBalance() {
    const reserved = this.amounts?.course_cash?.reserved ?? 0;
    const used = this.amounts?.course_cash?.used ?? 0;
    return reserved - used;
  }

  get requestedCourseCashSubtotal() {
    const sum = this.activeCourses
      .map((course) => course.courseTuition)
      .reduce((a, b) => {
        return a + b;
      }, 0);
    // This is in pennies, we should never have a decimal.
    // Rounding this here in case migrated data has decimals.
    return Math.round(sum);
  }

  get requestedCourseCashSubtotalLessScholarships() {
    const subtotal = Math.max(0, this.requestedCourseCashSubtotal - this.scholarshipsTotal);

    const template = this.tasProgramInstance?.tasProgramTemplate;
    if (template?.calculateTotalByPercent) {
      const percent = template.reimbursementPercentage ?? 100;
      return Math.round((subtotal * percent) / 100);
    }

    return Math.round(subtotal);
  }

  get requestedNonCourseCashSubtotal() {
    const sum = this.activeCourses
      .map((course) => course.nonTuitionExpenses)
      .reduce((a, b) => {
        return a + b;
      }, 0);
    // This is in pennies, we should never have a decimal.
    // Rounding this here in case migrated data has decimals.
    const subtotal = Math.round(sum);

    const template = this.tasProgramInstance?.tasProgramTemplate;
    if (template?.calculateTotalByPercent) {
      const percent = template.reimbursementPercentage ?? 100;
      return Math.round((subtotal * percent) / 100);
    }

    return subtotal;
  }

  get requestedBooksSubtotal() {
    const sum = this.activeCourses.map((course) => course.courseBooks).reduce((a, b) => a + b, 0);
    return Math.round(sum);
  }

  get requestedLabsSubtotal() {
    const sum = this.activeCourses.map((course) => course.courseLabs).reduce((a, b) => a + b, 0);
    return Math.round(sum);
  }

  get requestedSoftwareSubtotal() {
    const sum = this.activeCourses
      .map((course) => course.courseSoftware)
      .reduce((a, b) => a + b, 0);
    return Math.round(sum);
  }

  get requestedFeesSubtotal() {
    const sum = this.activeCourses.map((course) => course.courseFees).reduce((a, b) => a + b, 0);
    return Math.round(sum);
  }

  get requestedTotalCalculationFormulaNote() {
    if (this.tasProgramInstance?.tasProgramTemplate?.calculateTotalByPercent) {
      const reimbursementPercentage =
        this.tasProgramInstance?.tasProgramTemplate?.reimbursementPercentage;
      const prePercentageValue = calculatePrePercentageValue(
        this.requestedTotal,
        reimbursementPercentage
      );
      const formattedOriginalTotal = this.intl.formatNumber(prePercentageValue / 100, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      });
      const formattedTotal = this.intl.formatNumber(this.requestedTotal / 100, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      });
      return this.intl.t(
        'tuition_assistance.application_details.reimbursement_calculation_by_percent_formula',
        {
          percent: reimbursementPercentage,
          originalTotalInDollars: formattedOriginalTotal,
          totalInDollars: formattedTotal,
        }
      );
    }

    if (this.tasProgramInstance?.tasProgramTemplate?.calculateTotalByCredit) {
      return this.explanationOfReimbursementByCredit;
    }

    if (this.tasProgramInstance?.tasProgramTemplate?.calculateTotalByGPA) {
      const formattedRequestedTotal = this.intl.formatNumber(this.requestedTotal / 100, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      });

      return this.intl.t(
        'tuition_assistance.application_details.reimbursement_calculation_by_gpa_formula',
        {
          gpa: this.gradePointAverage,
          requestedTotal: formattedRequestedTotal,
        }
      );
    }
    return '';
  }

  get explanationOfReimbursementByCredit() {
    if (!this.tasProgramInstance?.tasProgramTemplate?.calculateTotalByCredit) {
      return '';
    }

    // Reimbursement by credit calculations are done before subtracting scholarship amount
    const requestedTotalBeforeScholarships = this.requestedTotalAllCourses;

    const rate = this.tasProgramInstance?.tasProgramTemplate?.ratePerCredit || 0;
    const formattedRate = this.intl.formatNumber(rate / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
    const formattedTotalBeforeScholarships = this.intl.formatNumber(
      requestedTotalBeforeScholarships / 100,
      {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      }
    );
    return this.intl.t(
      'tuition_assistance.application_details.reimbursement_calculation_by_credit_formula',
      {
        rateInDollars: formattedRate,
        noOfCredits: requestedTotalBeforeScholarships / rate,
        totalInDollars: formattedTotalBeforeScholarships,
      }
    );
  }

  get scholarships() {
    return this.fields?.['SCHOLARSHIPS_RECEIVED']?.values || [];
  }

  get scholarshipsTotal() {
    const sum = this.scholarships.reduce((sum, scholarship) => {
      return sum + (scholarship.scholarshipAmount || 0);
    }, 0);
    // This is in pennies, we should never have a decimal.
    // Rounding this here in case migrated data has decimals.
    return Math.round(sum);
  }

  get includesScholarships() {
    return this.scholarshipsTotal > 0;
  }

  get approvedTotal() {
    return (getSingleValueForTasField('FINAL_APPROVED_TOTAL', this.fields) ??
      getSingleValueForTasField('PRE_APPROVED_TOTAL', this.fields)) as number | undefined;
  }

  getTotalByState(state: string): number {
    return this.tasAssistances.reduce(
      (total, assistance) => (assistance.state === state ? total + assistance.amount : total),
      0
    );
  }

  get isPayable() {
    return this.tasAssistances.some(
      (assistance) => assistance.state === 'TAS.AssistanceState.PAYABLE'
    );
  }

  get paidTotal() {
    return this.getTotalByState('TAS.AssistanceState.SETTLED');
  }

  get forgivenTotal() {
    return this.getTotalByState('TAS.AssistanceState.FORGIVEN');
  }

  get paidAndForgivenTotal() {
    return this.paidTotal + this.forgivenTotal;
  }

  get paidDate() {
    let earliestDate: string | null = null;

    this.tasAssistances.forEach((assistance: TasAssistanceModel) => {
      const settledDate = assistance.settledDate;

      if (settledDate && (!earliestDate || settledDate < earliestDate)) {
        earliestDate = settledDate;
      }
    });

    return earliestDate;
  }

  // Typed as a literal so consumers holding a
  // `TasApplication | TASProgramInstanceModel` union can narrow via
  // `app.modelName === 'tas-application'`.
  get modelName(): 'tas-application' {
    return 'tas-application';
  }

  get currentExpectedApproverEmployeeIds() {
    const approvalConfigType =
      this.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
        ? 'evidenceApproval'
        : 'coursesApproval';
    const reviewers: ApplicationApprovalSummaryApprover[] =
      this[approvalConfigType]?.current_reviewers || [];
    return new Set(reviewers.map((reviewer) => reviewer['employee_id']));
  }

  get allPossibleApproverEmployeeIdsForAllWorkflows() {
    const evidenceReviewers = this.evidenceApproval.possible_reviewers || [];
    const courseReviewers = this.coursesApproval.possible_reviewers || [];
    return new Set(
      [...evidenceReviewers, ...courseReviewers].map((reviewer) => reviewer['employee_id'])
    );
  }

  get isRefundRequested() {
    return this.paymentState === 'TAS.ApplicationPaymentState.REFUND_REQUESTED';
  }

  get amountOwed() {
    let amount = 0;
    for (const assistance of this.tasAssistances) {
      if (assistance.state === 'TAS.AssistanceState.REPAYABLE') {
        amount += Math.abs(assistance.amount);
      }
    }
    return amount;
  }

  get repaymentAmountOutstanding() {
    return this.paidTotal - this.amountOwed;
  }

  get isInCollections() {
    return (this.tags || []).some((tag) => tag.name === 'In Collections');
  }

  get shouldDisplayApprovedTotal() {
    // Only display approved total if the application is in state ATTEND and up
    return !this.inPreAttendState;
  }

  get inPreAttendState() {
    return this.preAttendStates.has(this.state);
  }

  get inAttendOrPriorState() {
    return this.preAttendAndAttendStates.has(this.state);
  }

  get shouldDisplayPaymentDetails() {
    const amounts = this.amounts;
    if (!amounts) return false;

    const hasPaymentFlags =
      amounts.flags?.request_payment_enabled || amounts.flags?.request_repayment_enabled;
    const hasPayments = (amounts.paid ?? 0) > 0 || (amounts.pending_payment ?? 0) > 0;
    const hasCourseCash =
      (amounts.course_cash?.reserved ?? 0) > 0 ||
      (amounts.course_cash?.used ?? 0) > 0 ||
      (amounts.course_cash?.refunded ?? 0) > 0;

    return hasPaymentFlags || hasPayments || hasCourseCash;
  }

  get isDormantState() {
    return this.dormantStates.has(this.state);
  }

  get certificateApplicationHasSubmitted() {
    return (
      this.tasProgramInstance?.tasProgramTemplate?.isCertificateProgram &&
      this.hasSubmittedApplicationStates.has(this.state)
    );
  }

  get applicationHasSubmittedForEvidenceApproval() {
    return this.postSubmitForPaymentStates.has(this.state);
  }

  get inRescindableState() {
    return this.rescindableStates.has(this.state);
  }

  // If the application allows for post-approval edits to approved total.
  get isEditableApprovedTotal() {
    const isUneditable =
      this.uneditableApprovedTotalPaymentStates.has(this.paymentState) ||
      this.uneditableApprovedTotalApplicationStates.has(this.state);
    return !isUneditable;
  }

  // If the application allows for request repayment (clawback).
  get canRequestRepayment() {
    return this.clawbackablePaymentStates.has(this.paymentState);
  }

  get latestPaymentDate() {
    // TODO: We should be looking at assistance always. This is a bug.
    // console.log('Do note use, assistance is the correct state');
    return this.paymentStateLog.at(-1)?.reason?.payment?.date;
  }

  get semesterCodeIsVisible() {
    return this.tasProgramInstance.tasProgramTemplate.semesterCodeIsVisible;
  }

  get semesterCode() {
    return getSingleValueForTasField('SEMESTER_CODE', this.fields);
  }

  get totalCredits() {
    return this.activeCourses.reduce((total, course) => total + course.courseCredit, 0);
  }

  /**
   * Whether this application's program uses credits-based limits
   * instead of monetary limits.
   */
  get isCreditBased(): boolean {
    const limitCapacity = getSingleValueForTasField(
      'LIMIT_CAPACITY',
      this.tasProgramInstance?.tasProgramTemplate?.fields
    );
    return limitCapacity === 'TAS.LimitCapacity.CREDITS';
  }

  get totalPassingCredits() {
    return this.activeCourses.reduce((total, course) => total + course.passingCredit, 0);
  }

  get fractionalPercentPassed(): number {
    if (this.totalCredits === 0) return 0;
    // If the application is in the PENDING_COURSES_APPROVAL state, the fractional percent
    // passed should be 1. This is because the course has to be completed before a grade
    // can be assigned and a true fractional percent passed can be calculated.
    if (this.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return 1;
    }
    const rawPercentage = this.totalPassingCredits / this.totalCredits;
    return parseFloat(rawPercentage.toFixed(2));
  }

  // Ensure we have a properly initialized TasClaimsFinancials object when reading data.
  // `fractionPercentPassed` is a dynamic value throughout the lifecycle of the application
  // so any new calculations must be made with the most current value.
  get claimsFinancialsCalculatorInput(): Readonly<TasClaimsFinancials> {
    const defaultCF = createDefaultClaimsFinancialsWithBenefitPercent(
      this.tasProgramInstance?.tasProgramTemplate?.fractionalBenefitPercentForClaimsFinancials,
      this.requestedTotal
    );
    const currentCF = this.fields['CLAIMS_FINANCIALS'].values[0] || {};
    return {
      ...defaultCF,
      ...currentCF,
      CF_FRACTIONAL_PERCENT_PASSED: this.fractionalPercentPassed,
    };
  }

  get assistanceHistory() {
    return this.history?.assistance_history || [];
  }

  get paymentsHistory() {
    return this.assistanceHistory.flatMap((group) => group.records ?? []);
  }

  get approvedTotalHistory() {
    const appHistory = this.history?.application_history || [];
    return appHistory.filter((item) => item.logType === 'approval');
  }

  get fullHistory() {
    const appHistory = this.history?.application_history ?? [];
    return [...appHistory, ...this.paymentsHistory];
  }

  get inRepayment() {
    // Check if there's still an outstanding refund balance
    const refundRequested = this.amounts?.refund_requested ?? 0;
    const refundPaidBack = this.amounts?.refund_paid_back ?? 0;
    const refundForgiven = this.amounts?.refund_forgiven ?? 0;
    const refundCancelled = this.amounts?.refund_cancelled ?? 0;

    const outstandingBalance =
      refundRequested - (refundPaidBack + refundForgiven + refundCancelled);
    return outstandingBalance > 0;
  }

  get fromState(): TasApplicationState {
    return (this.transitionLog?.at(-1)?.from as TasApplicationState) || '';
  }

  get fromPaymentState(): TasApplicationPaymentState {
    // console.log('Do note use, assistance is the correct state');
    return this.paymentStateLog?.at(-1)?.from as TasApplicationPaymentState;
  }

  recalculateClaimsFinancialsAfterAdjacentUpdate = (): TasClaimsFinancials => {
    const calculator = new ClaimsFinancialsCalculator(this.claimsFinancialsCalculatorInput);
    // @ts-expect-error: Genuinely confused. This does exist on here and the fields are application fields type.
    setSingleValueForTasField('CLAIMS_FINANCIALS', this.fields, calculator.results);
    return calculator.results;
  };

  toggleInCollections = async (): Promise<void | TagModel> => {
    if (this.isInCollections) {
      const tag = this.tags.find((tag) => tag.name === 'In Collections');
      if (tag) {
        await tag.destroyRecord();
      }
    } else {
      const tag = this.store.createRecord('tag', {
        name: 'In Collections',
        taggable: this,
        taggableType: 'TasApplication',
      }) as TagModel;
      await tag.save();
      return tag;
    }
  };

  loadHistory = async (): Promise<void | TasAppHistory> => {
    const history = await this.store.adapterFor('tas-application').getHistory(this);
    this.history = history.data;
  };

  preAttendStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.DEFINE_COURSES',
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.COURSES_APPROVED',
    'TAS.ApplicationState.COURSES_NOT_APPROVED',
    'TAS.ApplicationState.COURSES_REJECTED',
  ]);

  preAttendAndAttendStates: Set<TasApplicationState> = new Set([
    ...this.preAttendStates,
    'TAS.ApplicationState.ATTEND',
  ]);

  dormantStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.COURSES_REJECTED',
    'TAS.ApplicationState.EVIDENCE_REJECTED',
    'TAS.ApplicationState.ABANDONED',
  ]);

  canDeleteAppStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    'TAS.ApplicationState.DEFINE_COURSES',
  ]);

  clawbackablePaymentStates = new Set([
    'TAS.ApplicationPaymentState.REFUND_REQUESTED',
    'TAS.ApplicationPaymentState.PAID_IN_FULL',
    'TAS.ApplicationPaymentState.PAID_IN_PART',
  ]);

  uneditableApprovedTotalPaymentStates = new Set([
    'TAS.ApplicationPaymentState.REFUND_REQUESTED',
    'TAS.ApplicationPaymentState.PAID_IN_FULL',
  ]);

  uneditableApprovedTotalApplicationStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.COURSES_REJECTED',
    'TAS.ApplicationState.EVIDENCE_REJECTED',
    'TAS.ApplicationState.ABANDONED',
  ]);

  postSubmitForPaymentStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    'TAS.ApplicationState.EVIDENCE_APPROVED',
    'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
    'TAS.ApplicationState.EVIDENCE_REJECTED',
    'TAS.ApplicationState.FULFILLED',
    'TAS.ApplicationState.ABANDONED',
  ]);

  hasSubmittedApplicationStates: Set<TasApplicationState> = new Set([
    ...this.dormantStates,
    ...this.uneditableApprovedTotalApplicationStates,
    ...this.postSubmitForPaymentStates,
    'TAS.ApplicationState.FULFILLED',
    'TAS.ApplicationState.ATTEND',
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.COURSES_APPROVED',
    'TAS.ApplicationState.COURSES_NOT_APPROVED',
    'TAS.ApplicationState.COURSES_REJECTED',
  ]);

  approvedStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.COURSES_APPROVED',
    'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    'TAS.ApplicationState.ATTEND',
    'TAS.ApplicationState.FULFILLED',
    'TAS.ApplicationState.EVIDENCE_APPROVED',
  ]);

  paidStates: Set<string> = new Set([
    'TAS.ApplicationPaymentState.PAID_IN_FULL',
    'TAS.ApplicationPaymentState.PAID_IN_PART',
  ]);

  approverCanEditStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.ATTEND',
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.COURSES_APPROVED',
    'TAS.ApplicationState.COURSES_NOT_APPROVED',
    'TAS.ApplicationState.COURSES_REJECTED',
    'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    'TAS.ApplicationState.EVIDENCE_APPROVED',
    'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
    'TAS.ApplicationState.EVIDENCE_REJECTED',
  ]);

  restingStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.ABANDONED',
    'TAS.ApplicationState.FULFILLED',
    'TAS.ApplicationState.EVIDENCE_REJECTED',
    'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    'TAS.ApplicationState.ATTEND',
    'TAS.ApplicationState.COURSES_REJECTED',
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.DEFINE_COURSES',
  ]);

  rescindableStates: Set<TasApplicationState> = new Set([
    'TAS.ApplicationState.DEFINE_COURSES',
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.ATTEND',
    'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    'TAS.ApplicationState.FULFILLED',
  ]);

  get badgeIntent() {
    // Prioritize paymentState if in active repayment (outstanding balance > 0)
    // Otherwise fall back to application state
    let stateToFormat: TasApplicationState | TasApplicationPaymentState = this.state;
    if (this.inRepayment) {
      // Always show REFUND_REQUESTED intent when in repayment
      stateToFormat = 'TAS.ApplicationPaymentState.REFUND_REQUESTED';
    }
    const formattedState = formatStateKey(stateToFormat, this.fromState, this.typeClassification);
    return tasIntentMap[formattedState] || 'default';
  }

  get badgeLabel() {
    // Prioritize paymentState if in active repayment (outstanding balance > 0)
    // Otherwise fall back to application state
    let stateToFormat: TasApplicationState | TasApplicationPaymentState = this.state;
    if (this.inRepayment) {
      // Use paymentState if available, otherwise default to REFUND_REQUESTED
      stateToFormat = 'TAS.ApplicationPaymentState.REFUND_REQUESTED';
    }
    const formattedState = formatStateKey(stateToFormat, this.fromState, this.typeClassification);
    return tasStateLabelMap[formattedState] || '';
  }

  get hasWaiverApplied() {
    const attachmentWaived = getSingleValueForTasField(
      'WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT',
      this.fields
    );
    const approvalWaived = getSingleValueForTasField(
      'WAIVE_EVIDENCE_APPROVAL_REQUIREMENT',
      this.fields
    );
    return attachmentWaived === true || approvalWaived === true;
  }

  [Type] = 'tas-application' as const;
}
